import { queryUser } from "@/services/commonService";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "@tanstack/react-router";
import React, { createContext, useContext, useMemo } from "react";

export enum UserRole {
  ADMIN = "ADMIN",
  APPROVER = "APPROVER",
  MANAGER = "MANAGER",
  GUEST = "GUEST",
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  affiliatedDepartments: Array<{
    id: number;
    isHead: boolean;
  }>;
}

interface UserContextType {
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  isApprover: boolean;
  isManager: boolean;
  hasRole: (role: UserRole) => boolean;
}

const UserContext = createContext<UserContextType>({
  user: null,
  isLoading: true,
  isAdmin: false,
  isApprover: false,
  isManager: false,
  hasRole: () => false,
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const excludePaths = ["/error/401", "/error/403", "/error/404"];
  const isExcludedPath = excludePaths.includes(location.pathname);

  const { data: user, isLoading } = useQuery<User>({
    ...queryUser(),
    enabled: !isExcludedPath, // 401, 403, 404 페이지에서는 사용자 정보를 가져오지 않음
    staleTime: 5 * 60 * 1000, // 5분 동안 fresh 상태 유지
    gcTime: 29 * 60 * 1000, // 30분 동안 캐시 유지
  });

  const contextValue = useMemo(
    () => ({
      user: user || null,
      isLoading,
      isAdmin: user?.role === UserRole.ADMIN,
      isApprover: user?.role === UserRole.APPROVER,
      isManager: user?.role === UserRole.MANAGER,
      hasRole: (role: UserRole) => user?.role === role,
    }),
    [user, isLoading]
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
